import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import { useAuth } from '../../context/AuthContext';
import { formatLocalDate } from '../../utils/dateUtils';

import { IC } from './insightsTheme';
import HeroMetric from './components/HeroMetric';
import KpiTicker from './components/KpiTicker';
import ExecSummary from './components/ExecSummary';
import ActionsInProgress from './components/ActionsInProgress';
import LeversCard from './components/LeversCard';
import InactionWidget from './components/InactionWidget';
import YoyBySector from './components/YoyBySector';
import UpcomingCmAgenda from './components/UpcomingCmAgenda';
import KPIBox from '../../components/shared/KPIBox';

// ════════════════════════════════════════════════════════════════════
// Helpers
// ════════════════════════════════════════════════════════════════════

// Anonymized labels "Client 221" / "Client 221 - Address 221" / "Client 221 — Anything" → "Ubic. #221".
const humanize = (g = '') => {
  const m = String(g).match(/^Client\s+(\d+)\b/i);
  return m ? `Ubic. #${m[1]}` : g;
};

const lossToSev = (loss) => {
  const n = Number(loss) || 0;
  if (n >= 1000) return 'alta';
  if (n >= 200) return 'media';
  return 'baja';
};

const typeForGroup = (name = '') => {
  const n = name.toLowerCase();
  if (/stock|break|quiebre|out/.test(n)) return 'stockout';
  if (/price|precio|margen/.test(n)) return 'price';
  if (/dead|muert/.test(n)) return 'dead';
  if (/fill|reposici/.test(n)) return 'fill';
  if (/target|objet/.test(n)) return 'target';
  if (/season|verano|estacion/.test(n)) return 'seasonal';
  if (/opportun|oport/.test(n)) return 'opportunity';
  if (/perf|rendim/.test(n)) return 'perf';
  return 'warn';
};

const fmtEur = (v) =>
  new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(Number(v) || 0);

// Hero series constructed from real weekly YTD data. Projection is OPTIONAL and only
// present if the orchestrator has monthly-profit history to base it on — no silent mocks.
const buildHeroSeries = (weeklyRows, { withProjection } = { withProjection: false }) => {
  const rows = (weeklyRows || [])
    .map((r) => ({ week: Number(r.week), value: Number(r.value) || 0 }))
    .filter((r) => !Number.isNaN(r.week))
    .sort((a, b) => a.week - b.week);

  if (rows.length === 0) {
    return { sem: [], mes: [], trim: [], ytd: [], y1: [] };
  }

  // Dress each window with a projection segment if we have one to draw.
  const dress = (pts, projectionVal) => {
    const base = pts.map((p) => ({ d: p.d, a: p.a, f: null }));
    if (withProjection && projectionVal != null && base.length > 0) {
      base[base.length - 1].f = base[base.length - 1].a;
      base.push({ d: '→', a: null, f: projectionVal });
    }
    return base;
  };

  // sem: last 7 weeks raw
  const semRows = rows.slice(-7);
  const sem = dress(
    semRows.map((r) => ({ d: `W${r.week}`, a: Math.round(r.value) })),
    withProjection ? Math.round(semRows[semRows.length - 1].value * 1.05) : null
  );

  // mes: last 4 weeks
  const mesRows = rows.slice(-4);
  const mes = dress(
    mesRows.map((r, i) => ({ d: `W${r.week}`, a: Math.round(r.value) })),
    withProjection ? Math.round(mesRows[mesRows.length - 1].value * 1.05) : null
  );

  // trim: quarter-to-date (13 ISO weeks per quarter) of the latest completed week, CUMULATIVE
  const quarterOf = (week) => Math.min(4, Math.ceil(week / 13));
  const latestQuarter = quarterOf(rows[rows.length - 1].week);
  const trimRows = rows.filter((r) => quarterOf(r.week) === latestQuarter);
  let cum = 0;
  const trimPts = trimRows.map((r) => {
    cum += Math.round(r.value);
    return { d: `W${r.week}`, a: cum };
  });
  const trim = dress(trimPts, null); // no projection for cumulative

  // ytd: aggregate by month-like buckets (~4-5 weeks each)
  const bucketSize = Math.max(1, Math.ceil(rows.length / 3));
  const ytdBuckets = [];
  for (let i = 0; i < 3; i += 1) {
    const slice = rows.slice(i * bucketSize, (i + 1) * bucketSize);
    if (slice.length === 0) continue;
    const sum = slice.reduce((s, r) => s + r.value, 0);
    ytdBuckets.push({ d: `P${i + 1}`, a: Math.round(sum) });
  }
  const ytd = dress(ytdBuckets, null);

  // y1: calendar quarters (13 ISO weeks each; week 53 folds into Q4)
  const y1Buckets = [];
  for (let q = 1; q <= 4; q += 1) {
    const slice = rows.filter((r) => quarterOf(r.week) === q);
    if (slice.length === 0) continue;
    const sum = slice.reduce((s, r) => s + r.value, 0);
    y1Buckets.push({ d: `Q${q}`, a: Math.round(sum) });
  }
  const y1 = dress(y1Buckets, null);

  return { sem, mes, trim, ytd, y1 };
};

// ════════════════════════════════════════════════════════════════════
// PerformanceTab
// ════════════════════════════════════════════════════════════════════

export const PerformanceTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, isAuthenticated } = useAuth();

  const [heroWindow, setHeroWindow] = useState('trim');
  const [heroMetric, setHeroMetric] = useState('ben');

  // Real-data state
  const [currentMonthMetrics, setCurrentMonthMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [ytdData, setYtdData] = useState({ ben: [], ing: [], uds: [] });
  const [ytdLoading, setYtdLoading] = useState(true);
  const [problemsSummary, setProblemsSummary] = useState(null);
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [expSummary, setExpSummary] = useState({ pending: [], accepted: [] });
  const [expLoading, setExpLoading] = useState(true);

  const [yoyData, setYoyData] = useState(null);
  const [yoyLoading, setYoyLoading] = useState(true);
  const [agenda, setAgenda] = useState(null);
  const [runningSummary, setRunningSummary] = useState(null);

  const clientId = currentUser?.customer_id;

  // Re-targeted navigation: every interactive element lands on a real dev page.
  // ExperimentManagement / ExperimentsOverview consume location.state.filters
  // ({group, location, machine}); Diagnostics is reached via its URL tab slug.
  const goto = useCallback((path, state) => {
    navigate(path, state ? { state } : undefined);
  }, [navigate]);

  // ══════════════ Fetches
  useEffect(() => {
    if (!isAuthenticated || !clientId) return;
    const token = localStorage.getItem('access_token');
    const headers = createAuthHeader(token);
    // Cancelled on unmount: the browser allows only 6 open requests per API host (HTTP/1.1), so
    // slow dashboard calls left running would queue the next page's requests behind them.
    const controller = new AbortController();
    const { signal } = controller;
    const enc = encodeURIComponent(clientId);

    setMetricsLoading(true);
    axios
      .get(`${API_BASE_URL}/metrics/current-week?clientId=${enc}`, { headers, signal })
      .then((r) => setCurrentMonthMetrics(r.data))
      .catch(() => {})
      .finally(() => setMetricsLoading(false));

    setYtdLoading(true);
    Promise.all(
      ['units', 'revenue', 'profit'].map((ind) =>
        axios
          .post(
            `${API_BASE_URL}/metrics/year-to-date?clientId=${enc}`,
            { indicator: ind },
            { headers, signal }
          )
          .then((r) => ({ ind, rows: Array.isArray(r.data?.data) ? r.data.data : [] }))
          .catch(() => ({ ind, rows: [] }))
      )
    )
      .then((results) => {
        const out = { ben: [], ing: [], uds: [] };
        const keyMap = { units: 'uds', revenue: 'ing', profit: 'ben' };
        results.forEach(({ ind, rows }) => {
          out[keyMap[ind]] = rows;
        });
        setYtdData(out);
      })
      .finally(() => setYtdLoading(false));

    axios
      .get(`${API_BASE_URL}/problems/summary?clientId=${enc}`, { headers, signal })
      .then((r) => setProblemsSummary(r.data || null))
      .catch(() => {});

    axios
      .get(`${API_BASE_URL}/alerts/summary?clientId=${enc}`, { headers, signal })
      .then((r) => setAlertsSummary(r.data || null))
      .catch(() => {});

    // Experiments summary (pending + accepted for current + next month)
    setExpLoading(true);
    {
      const now = new Date();
      const startDate = formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 1));
      const endDate = formatLocalDate(new Date(now.getFullYear(), now.getMonth() + 2, 0));
      axios
        .get(
          `${API_BASE_URL}/experiments/summary?clientId=${enc}&startDate=${encodeURIComponent(
            startDate
          )}&endDate=${encodeURIComponent(endDate)}`,
          { headers, signal }
        )
        .then((r) => {
          const data = r.data || {};
          setExpSummary({
            pending: Array.isArray(data.pending) ? data.pending : [],
            accepted: Array.isArray(data.accepted) ? data.accepted : [],
          });
        })
        .catch(() => setExpSummary({ pending: [], accepted: [] }))
        .finally(() => setExpLoading(false));
    }

    setYoyLoading(true);
    axios
      .get(`${API_BASE_URL}/sales-patterns/yoy-comparison?clientId=${enc}`, { headers, signal })
      .then((r) => setYoyData(r.data || null))
      .catch(() => setYoyData(null))
      .finally(() => setYoyLoading(false));

    axios
      .get(`${API_BASE_URL}/experiments/upcoming-message?clientId=${enc}`, { headers, signal })
      .then((r) => setAgenda(r.data || null))
      .catch(() => setAgenda(null));

    // Running-experiment KPI aggregates for the tiles (real endpoint, replaces the demo mock).
    axios
      .get(`${API_BASE_URL}/experiments/running-summary?clientId=${enc}`, { headers, signal })
      .then((r) => setRunningSummary(r.data || null))
      .catch(() => setRunningSummary(null));
    return () => controller.abort();
  }, [clientId, isAuthenticated]);

  // ══════════════ Derived

  // Hero series — no forged projection. Shown actual only.
  const heroSeries = useMemo(() => {
    const out = {};
    ['ben', 'ing', 'uds'].forEach((m) => {
      const built = buildHeroSeries(ytdData[m]);
      Object.entries(built).forEach(([k, arr]) => {
        if (!out[k]) out[k] = {};
        out[k][m] = arr;
      });
    });
    return out;
  }, [ytdData]);

  const yoyPct = useMemo(() => {
    if (!currentMonthMetrics?.deltas) return null;
    const map = { ben: 'profitPct', ing: 'revenuePct', uds: 'unitsPct' };
    const v = currentMonthMetrics.deltas[map[heroMetric]];
    return v == null ? null : Math.round(Number(v));
  }, [currentMonthMetrics, heroMetric]);

  // KPI ticker — 3 real slots, all from real data.
  const tickerItems = useMemo(() => {
    const cm = currentMonthMetrics || {};
    const d = cm.deltas || {};
    const revenueWeek = cm.revenue != null ? Math.round(Number(cm.revenue) / 4.3) : null;
    const profitWeek = cm.profit != null ? Math.round(Number(cm.profit) / 4.3) : null;
    const ticketMedio = cm.revenue && cm.units ? Number(cm.revenue) / Number(cm.units) : null;

    // Sparklines: the last 8 completed weeks of the real weekly series.
    const lastWeeks = (rows) => (rows || []).slice(-8);
    const valuesOf = (rows) => lastWeeks(rows).map((r) => Number(r.value) || 0);
    const ticketSpark = lastWeeks(ytdData.ing).map((r) => {
      const units = Number((ytdData.uds || []).find((u) => Number(u.week) === Number(r.week))?.value) || 0;
      return units ? Number(r.value) / units : 0;
    });

    const trendOf = (p) => (p == null ? 'flat' : p > 0 ? 'up' : p < 0 ? 'down' : 'flat');

    return [
      {
        label: t('insights_v2_ticker_revenue_week'),
        value: revenueWeek != null ? `${fmtEur(revenueWeek)}€` : '—',
        deltaLabel:
          d.revenuePct != null
            ? `${d.revenuePct >= 0 ? '+' : ''}${Math.round(d.revenuePct)}% ${t('insights_v2_hero_yoy')}`
            : '',
        trend: trendOf(d.revenuePct),
        spark: valuesOf(ytdData.ing),
      },
      {
        label: t('insights_v2_ticker_profit_week'),
        value: profitWeek != null ? `${fmtEur(profitWeek)}€` : '—',
        deltaLabel:
          d.profitPct != null
            ? `${d.profitPct >= 0 ? '+' : ''}${Math.round(d.profitPct)}% ${t('insights_v2_hero_yoy')}`
            : '',
        trend: trendOf(d.profitPct),
        spark: valuesOf(ytdData.ben),
      },
      {
        label: t('insights_v2_ticker_ticket'),
        value:
          ticketMedio != null
            ? `${ticketMedio.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`
            : '—',
        deltaLabel: '', // no tenant-level ticket-medio delta endpoint
        trend: 'flat',
        spark: ticketSpark,
      },
    ];
  }, [currentMonthMetrics, ytdData, t]);

  // Acciones en curso — the actually-running experiments (status='running'), from
  // /experiments/running-summary's per-experiment list. Health colour reflects gross-profit delta;
  // the row carries the real "Client N" group so a click lands in the "En Ejecución" column.
  const actions = useMemo(() => {
    const list = runningSummary?.experiments || [];
    // "COMBO A + B" → "B": the combo's own name repeats the original product, so only the
    // second half is new information next to it.
    const comboSecond = (name) => {
      const m = (name || '').match(/^COMBO\s+(.+?)\s*\+\s*(.+)$/);
      return m ? m[2].trim() : name || null;
    };
    return list.map((e, idx) => {
      const delta = e.deltaGrossProfit;
      // A replacement/combo earns its euros with the INCOMING product, so the row must name both
      // ends of the swap — labelling it with the outgoing product alone credits the wrong product.
      // Same "original → new" / "original + second" convention as the experiments table.
      let counterpart = null;
      let sep = null;
      if (e.experimentType === 'reemplazo_producto' && e.newProductName && e.newProductName !== e.productName) {
        counterpart = e.newProductName;
        sep = '→';
      } else if (e.experimentType === 'promo_combo_combinado' && e.newProductName) {
        counterpart = comboSecond(e.newProductName);
        sep = '+';
      }
      return {
        id: `#${String(e.id ?? idx + 1).slice(-4).padStart(4, '0')}`,
        n: e.productName || (e.group ? humanize(e.group) : '—'),
        nCounterpart: counterpart,
        nSep: sep,
        hp: delta == null ? 'amb' : delta >= 0 ? 'grn' : 'red',
        // exact delta — the row display rounds it, but the "total benefit" footer sums the exact
        // values so it equals the tile's incremental gross profit (no per-row rounding drift).
        dB: delta != null ? delta : null,
        elapsed: e.elapsedWeeks ?? 0,
        dur: e.durationWeeks ?? 1,
        rawGroup: e.group || null,
        productId: e.dProductId || null,
      };
    });
  }, [runningSummary]);

  // Levers — top 3 problem groups by estimated loss
  const levers = useMemo(() => {
    const groups = (problemsSummary?.groups || [])
      .slice()
      .sort((a, b) => Number(b.estimatedLoss || 0) - Number(a.estimatedLoss || 0))
      .slice(0, 3);
    const totalLoss = groups.reduce((s, g) => s + Number(g.estimatedLoss || 0), 0) || 1;
    return groups.map((g) => ({
      n: humanize(g.group),
      d: t('insights_v2_levers_problem_count', { count: g.count }),
      impactLabel: `+${fmtEur(g.estimatedLoss)}€`,
      impactSuffix: t('insights_v2_levers_impact_suffix'),
      pct: Math.round((Number(g.estimatedLoss || 0) / totalLoss) * 100),
      onClick: () => goto('/diagnostics/problems', { filters: { group: g.group || '' } }),
    }));
  }, [problemsSummary, goto, t]);

  // Inaction real numbers — prefer the untruncated metadata totals (groups are
  // sliced to top 5 by the backend, so summing them undercounts the network-wide loss).
  const { alertsMonthlyLoss, realMonthlyLoss } = useMemo(() => {
    const sumGroup = (g) => g.reduce((s, x) => s + Number(x.estimatedLoss || 0), 0);
    const alertsLoss =
      alertsSummary?.metadata?.totalEstimatedLoss != null
        ? Number(alertsSummary.metadata.totalEstimatedLoss)
        : sumGroup(alertsSummary?.groups || []);
    const problemsLoss =
      problemsSummary?.metadata?.totalEstimatedLoss != null
        ? Number(problemsSummary.metadata.totalEstimatedLoss)
        : sumGroup(problemsSummary?.groups || []);
    return { alertsMonthlyLoss: alertsLoss, realMonthlyLoss: alertsLoss + problemsLoss };
  }, [alertsSummary, problemsSummary]);

  const alertCount = useMemo(() => {
    return (
      alertsSummary?.metadata?.totalAlerts ||
      (alertsSummary?.groups || []).reduce((s, g) => s + Number(g.count || 0), 0) ||
      0
    );
  }, [alertsSummary]);

  // ExecSummary — narrative composed from real numbers via i18n templates.
  const execProps = useMemo(() => {
    const cm = currentMonthMetrics || {};
    // The headline says "vs last year", so it must use the real YoY comparison —
    // cm.deltas from /metrics/current-week is month-over-month, not YoY.
    const yoyPctReal =
      yoyData?.overall?.percentChange != null
        ? Math.round(Number(yoyData.overall.percentChange))
        : null;

    const thinkSteps = [
      t('insights_v2_exec_think_1'),
      t('insights_v2_exec_think_2'),
      t('insights_v2_exec_think_3'),
      t('insights_v2_exec_think_4'),
      t('insights_v2_exec_think_5'),
      t('insights_v2_exec_think_6'),
    ];

    const paragraphs = [];
    const highlights = [];

    // Para 1 — headline
    if (cm.profit != null) {
      const revStr = `${fmtEur(cm.profit)}€`;
      const yoyStr = yoyPctReal != null ? `${yoyPctReal > 0 ? '+' : ''}${yoyPctReal}%` : '';
      const key =
        yoyPctReal == null || yoyPctReal === 0
          ? 'insights_v2_exec_para_headline_neutral'
          : yoyPctReal > 0
          ? 'insights_v2_exec_para_headline_up'
          : 'insights_v2_exec_para_headline_down';
      paragraphs.push(t(key, { revenue: revStr, yoy: yoyStr }));
      highlights.push(revStr);
      if (yoyStr) highlights.push(yoyStr);
    } else {
      paragraphs.push(t('insights_v2_exec_para_no_data'));
    }

    // Para 2 — top problem
    const topGroup = (problemsSummary?.groups || [])
      .slice()
      .sort((a, b) => Number(b.estimatedLoss || 0) - Number(a.estimatedLoss || 0))[0];
    if (topGroup) {
      const lossStr = `${fmtEur(topGroup.estimatedLoss)}€`;
      paragraphs.push(
        t('insights_v2_exec_para_top_problem', {
          group: humanize(topGroup.group),
          count: topGroup.count,
          loss: lossStr,
        })
      );
      highlights.push(humanize(topGroup.group), lossStr);
    }

    // Para 3 — inaction
    if (alertCount > 0 && alertsMonthlyLoss > 0) {
      const monthlyStr = `${fmtEur(alertsMonthlyLoss)}€`;
      const dailyStr = `${fmtEur(Math.round(alertsMonthlyLoss / 30))}€`;
      paragraphs.push(
        t('insights_v2_exec_para_inaction', {
          alertCount,
          monthlyLoss: monthlyStr,
          dailyLoss: dailyStr,
        })
      );
      highlights.push(monthlyStr, dailyStr);
    }

    // Para 4 — actions, from the real /experiments/summary counts.
    const active = (expSummary.accepted || []).length;
    const pending = (expSummary.pending || []).length;
    if (active + pending > 0) {
      paragraphs.push(t('insights_v2_exec_para_actions', { active, pending }));
    }

    // Contextual drill-in CTAs — re-targeted to real dev pages.
    const ctas = [];
    if (topGroup) {
      ctas.push({
        label: t('insights_v2_exec_cta_open_top_problem', { group: humanize(topGroup.group) }),
        onClick: () => goto('/diagnostics/problems', { filters: { group: topGroup.group || '' } }),
      });
    }
    if (active + pending > 0) {
      ctas.push({
        label: t('insights_v2_exec_cta_review_actions'),
        onClick: () => goto('/experiments-overview'),
      });
    }
    if (alertCount > 0) {
      ctas.push({
        label: t('insights_v2_exec_cta_open_alerts'),
        onClick: () => goto('/diagnostics/alerts'),
      });
    }

    return {
      title: t('insights_v2_exec_title'),
      thinkSteps,
      paragraphs,
      highlights,
      ctas,
    };
  }, [currentMonthMetrics, yoyData, problemsSummary, alertCount, alertsMonthlyLoss, expSummary, t, goto]);

  // "Próximos 7 días" — only experiments with status='accepted' (approved but
  // not yet started) starting in the next 7 days, from the live feed.
  const agendaUpcoming = useMemo(() => {
    const todayMs = Date.now();
    const sevenDaysMs = todayMs + 7 * 24 * 3600 * 1000;
    const within7d = (iso) => {
      if (!iso) return false;
      const dd = new Date(iso).getTime();
      return dd > todayMs && dd <= sevenDaysMs;
    };
    const accepted = (expSummary.accepted || []).filter((e) => within7d(e.dateFrom));
    return accepted.slice(0, 3).map((e, idx) => ({
      id: e.id || e.experimentId || `agenda-${idx}`,
      productName: e.productName || e.description || e.originalProduct?.name || null,
      machineId: e.machineId || e.storeId || null,
      productId: e.originalProduct?.id || e.productId || null,
      group: e.group || null,
      dateFrom: e.dateFrom,
      status: 'accepted',
    }));
  }, [expSummary]);

  // 3 KPI tiles — aggregate of running experiments only, from /experiments/running-summary.
  const step5Totals = useMemo(
    () => runningSummary || { hasData: false, runningCount: 0 },
    [runningSummary]
  );

  const curQ = useMemo(() => `Q${Math.floor(new Date().getMonth() / 3) + 1}`, []);
  const userName =
    currentUser?.displayName || currentUser?.firstName || currentUser?.name || '';

  return (
    <Box
      className="insights-root-v2"
      sx={{
        background: IC.bg,
        minHeight: '100vh',
        fontFamily: (th) => th.typography.fontFamily,
      }}
    >
      <style>{`
        .insights-root-v2 button:focus { outline: none; }
        .insights-root-v2 button:focus-visible { outline: 2px solid ${IC.pri}; outline-offset: 2px; border-radius: 4px; }
        .insights-root-v2 [tabindex="0"]:focus-visible { outline: 2px solid ${IC.pri}; outline-offset: 2px; border-radius: 4px; }
      `}</style>

      <HeroMetric
        window={heroWindow}
        setWindow={setHeroWindow}
        metric={heroMetric}
        setMetric={setHeroMetric}
        series={heroSeries}
        yoyPct={yoyPct}
        hasProjection={false}
        curQ={curQ}
        greeting={userName ? t('hello', { name: userName }) : ''}
        loading={metricsLoading || ytdLoading}
      />

      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: 6 }, py: 2.5 }}>
        {/* Row 1 — KPI ticker (3 KPIs) directly under the hero plot */}
        <Box sx={{ mb: 2 }}>
          <KpiTicker items={tickerItems} />
        </Box>

        {/* Row 1b — Running experiments KPI tiles (aggregate return of running experiments) */}
        {step5Totals.hasData && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                {t('insights_v2_running_kpis_title')}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                {t('insights_v2_actions_count', { count: step5Totals.runningCount })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
              <Box
                sx={{ flex: 1, cursor: 'pointer' }}
                onClick={() => goto('/experiments-overview')}
                role="button"
                tabIndex={0}
                aria-label={t('insights_v2_exec_cta_review_actions')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goto('/experiments-overview'); } }}
              >
                <KPIBox
                  title={t('totalUnits')}
                  value={step5Totals.totalUnits}
                  delta={step5Totals.totalDeltaUnits}
                  percentage={step5Totals.unitsPercentage}
                  type="units"
                  showComparisonHint={false}
                />
              </Box>
              <Box
                sx={{ flex: 1, cursor: 'pointer' }}
                onClick={() => goto('/experiments-overview')}
                role="button"
                tabIndex={0}
                aria-label={t('insights_v2_exec_cta_review_actions')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goto('/experiments-overview'); } }}
              >
                <KPIBox
                  title={t('totalRevenue')}
                  value={step5Totals.totalRevenue}
                  delta={step5Totals.totalDeltaRevenue}
                  percentage={step5Totals.revenuePercentage}
                  type="currency"
                  showComparisonHint={false}
                />
              </Box>
              <Box
                sx={{ flex: 1, cursor: 'pointer' }}
                onClick={() => goto('/experiments-overview')}
                role="button"
                tabIndex={0}
                aria-label={t('insights_v2_exec_cta_review_actions')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goto('/experiments-overview'); } }}
              >
                <KPIBox
                  title={t('totalGrossProfit')}
                  value={step5Totals.totalGrossProfit}
                  delta={step5Totals.totalDeltaGrossProfit}
                  percentage={step5Totals.grossProfitPercentage}
                  type="currency"
                  showComparisonHint={false}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Row 2 — Analista CM summary */}
        <Box sx={{ mb: 2 }}>
          <ExecSummary
            title={execProps.title}
            thinkSteps={execProps.thinkSteps}
            paragraphs={execProps.paragraphs}
            highlights={execProps.highlights}
            ctas={execProps.ctas}
          />
        </Box>

        {/* Row 3 — Ubicaciones y sus oportunidades + Inaction widget */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'stretch', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <LeversCard levers={levers} title={t('insights_v2_locations_opportunities_title')} />
          </Box>
          {(alertCount > 0 || realMonthlyLoss > 0) && (
            <InactionWidget
              alertCount={alertCount}
              agingDays={alertsSummary?.metadata?.avgAgingDays ?? null}
              monthlyLoss={realMonthlyLoss}
              width={300}
              onAlertsClick={() => goto('/diagnostics/alerts')}
              onLossClick={() => goto('/diagnostics/alerts')}
            />
          )}
        </Box>

        {/* Row 4 — Acciones en curso + Próximos 7 días */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'stretch', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <ActionsInProgress
            actions={actions}
            total={runningSummary?.runningCount ?? actions.length}
            onTrack={actions.filter((a) => a.hp === 'grn').length}
            loading={runningSummary === null}
            onSeeMore={() => goto('/experiments-overview')}
            onRowClick={(a) =>
              // Pre-filter by group only: /experiments group id IS the "Client N" name (matches
              // exp.group), but its machine filter expects a numeric store id, not the alias we carry —
              // passing the alias would zero out the view, so we omit it.
              goto('/experiments', a.rawGroup ? { filters: { group: a.rawGroup } } : undefined)
            }
          />
          <UpcomingCmAgenda
            data={agenda}
            upcoming={agendaUpcoming}
            onSeeMore={() => goto('/experiments')}
            onProseClick={() => goto('/experiments')}
            onCountdownClick={() => goto('/experiments')}
            onExperimentClick={(exp) =>
              goto('/experiments', exp?.group ? { filters: { group: exp.group } } : undefined)
            }
          />
        </Box>

        {(yoyData?.sectors?.length || 0) > 0 && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <YoyBySector data={yoyData} loading={yoyLoading} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Default export preserved for navigation.
const Insights = () => (
  <Box sx={{ background: IC.bg }}>
    <PerformanceTab />
  </Box>
);

export default Insights;
